import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styles from 'styles/calendar.module.scss';
import { Icon, Dropdown } from 'components';
import Moment from 'moment';
import { extendMoment } from 'moment-range';
const moment = extendMoment(Moment);

const Calendar = ({ range, onChange }) => {
	const [activePicker, setActivePicker] = useState('days');
	const [dateObject, setDateObject] = useState(moment());
	const [month, setMonth] = useState(moment(dateObject).format('MMMM'));
	const [year, setYear] = useState(parseInt(moment(dateObject).format('YYYY')));
	const [lastMonth, setLastMonth] = useState(
		moment(dateObject)
			.subtract(1, 'months')
			.format('MMMM')
	);
	const [nextMonth, setNextMonth] = useState(
		moment(dateObject)
			.add(1, 'months')
			.format('MMMM')
	);
	useEffect(() => {
		setDateObject(moment(`${month}/${year}`));
		const last_month = moment(`${month}/${year}`)
			.subtract(1, 'months')
			.format('MMMM');
		const next_month = moment(`${month}/${year}`)
			.add(1, 'months')
			.format('MMMM');
		setLastMonth(last_month);
		setNextMonth(next_month);
	}, [month, year]);

	useEffect(() => {
		if (!range) {
			setMonth(moment().format('MMMM'));
			setYear(moment().format('YYYY'));
		} else if (Array.isArray(range) && range.length === 2) {
			setMonth(moment(range[0]).format('MMMM'));
			setYear(moment(range[0]).format('YYYY'));
		}
	}, [range]);

	return (
		<div className={styles.calendar}>
			<div className={styles.header}>
				<Icon
					size="lg"
					icon="caret-left"
					onClick={() => {
						if (lastMonth === 'December') setYear(year - 1);
						setMonth(lastMonth);
					}}
				/>
				<div className={styles.selects}>
					<b onClick={() => setActivePicker('months')}>{month}</b>
					<b onClick={() => setActivePicker('years')}>{year}</b>
				</div>
				<Icon
					size="lg"
					icon="caret-right"
					onClick={() => {
						if (nextMonth === 'January') setYear(year + 1);
						setMonth(nextMonth);
					}}
				/>
			</div>
			{activePicker === 'days' && <DaysPicker {...{ dateObject, range, onChange }} />}
			{activePicker === 'months' && (
				<MonthPicker
					active={month}
					onChange={month => {
						setMonth(month);
						setActivePicker('days');
					}}
				/>
			)}
			{activePicker === 'years' && (
				<YearsPicker
					active={year}
					onChange={year => {
						setYear(year);
						setActivePicker('days');
					}}
				/>
			)}
		</div>
	);
};

Calendar.propTypes = {};

Calendar.defaultProps = {};

export default Calendar;

const DaysPicker = ({ dateObject, range, onChange }) => {
	const [selectedRange, setSelectedRangeState] = useState(range);
	const [isSliding, setIsSliding] = useState(false);
	const isArray = length => array => Array.isArray(array) && array.length === length;
	const setSelectedRange = (range, fireOnChange = true) => {
		setSelectedRangeState(range);
		if (fireOnChange) {
			const formatRange = isArray(2)(range)
				? moment.range([moment(range[0]).startOf('day'), moment(range[1]).endOf('day')]).toDate()
				: null;
			onChange(formatRange);
		}
	};
	const [tempRange, setTempRange] = useState(null);
	const [previewRange, setPreviewRange] = useState(null);
	const [mouseIsDown, setMouseIsDown] = useState(false);

	const weekDayShort = moment.weekdaysShort();
	const monthDaysCount = moment(dateObject).daysInMonth();
	const currentMonthIsActive = moment(dateObject).format('MM/YYYY') === moment().format('MM/YYYY');
	const currentDay = currentMonthIsActive ? parseInt(moment().format('D')) : null;
	const firstDayOfMonth = parseInt(
		moment(dateObject)
			.startOf('month')
			.format('d')
	);
	const lastMonthDaysCount = moment(dateObject)
		.subtract(1, 'months')
		.daysInMonth();

	const handleMouseDown = date => {
		setMouseIsDown(true);
		if (!tempRange) {
			setTempRange([new Date(date)]);
			setSelectedRange(null);
		} else if (isArray(1)(tempRange)) {
			const newSelectedRange = tempRange.concat([new Date(date)]).sort((a, b) => a.getTime() - b.getTime());
			setSelectedRange(newSelectedRange);
			setTempRange(null);
			setPreviewRange(null);
		}
	};
	const handleMouseUp = () => {
		setMouseIsDown(false);
		setPreviewRange(null);
		if (isArray(2)(selectedRange)) setTempRange(null);
		if (isSliding) {
			setIsSliding(false);
			setSelectedRange(selectedRange);
		}
	};

	const handleMouseOver = date => {
		if (isArray(1)(tempRange) && (!selectedRange || mouseIsDown)) {
			const newSelectedRange = tempRange.concat([new Date(date)]).sort((a, b) => a.getTime() - b.getTime());
			if (mouseIsDown) {
				setIsSliding(true);
				setSelectedRange(newSelectedRange, false);
				setPreviewRange(null);
			} else setPreviewRange(newSelectedRange);
		}
	};

	const isOnRange = date => {
		const output = { selected: false, isFirst: false, isLast: false };
		if (!selectedRange) return output;
		const [startDate, endDate] = selectedRange;
		const momentRange = moment().range(startDate, endDate);
		const selected = momentRange.contains(new Date(date));
		if (!selected) return output;
		const isFirst = moment(date).format('MM/DD/YYYY') === moment(startDate).format('MM/DD/YYYY');
		const isLast = moment(date).format('MM/DD/YYYY') === moment(endDate).format('MM/DD/YYYY');
		return { selected, isFirst, isLast };
	};

	const isOnPreviewRange = date => {
		const output = { preview: false, isFirst: false, isLast: false };
		if (!previewRange) return output;
		const [startDate, endDate] = previewRange;
		const momentRange = moment().range(startDate, endDate);
		const preview = isArray(2)(previewRange) ? momentRange.contains(new Date(date)) : false;
		if (!preview) return output;
		const isFirst = moment(date).format('MM/DD/YYYY') === moment(startDate).format('MM/DD/YYYY');
		const isLast = moment(date).format('MM/DD/YYYY') === moment(endDate).format('MM/DD/YYYY');
		return { preview, isFirst, isLast };
	};

	useEffect(() => {
		if (!range) {
			setSelectedRange(null);
		} else if (
			isArray(2)(range) &&
			(!selectedRange || (isArray(2)(selectedRange) && !moment.range(range).isSame(moment.range(selectedRange))))
		) {
			setSelectedRange(range);
		}
	}, [range]);

	let blankDays = new Array(firstDayOfMonth)
		.fill(0)
		.map((_, i) => (
			<Day
				value={lastMonthDaysCount - i}
				isOnRange={isOnRange}
				isOnPreviewRange={isOnPreviewRange}
				setMouseIsDown={setMouseIsDown}
				dateObject={dateObject}
				onMouseDown={date => handleMouseDown(date)}
				onMouseUp={() => handleMouseUp()}
				onMouseOver={date => handleMouseOver(date)}
				month={moment(dateObject).subtract(1, 'months')}
				empty={true}
				key={i}
			/>
		))
		.reverse();

	let daysInMonth = new Array(monthDaysCount).fill(0).map((_, i) => {
		let attrs = currentDay && currentDay === i + 1 ? { 'data-today': true } : {};
		return (
			<Day
				isOnRange={isOnRange}
				isOnPreviewRange={isOnPreviewRange}
				setMouseIsDown={setMouseIsDown}
				value={i + 1}
				dateObject={dateObject}
				onMouseDown={date => handleMouseDown(date)}
				onMouseUp={() => handleMouseUp()}
				onMouseOver={date => handleMouseOver(date)}
				month={moment(dateObject)}
				key={firstDayOfMonth + i}
			/>
		);
	});
	let totalSlots = [...blankDays, ...daysInMonth];
	if (totalSlots.length < 35) {
		totalSlots = totalSlots.concat(
			new Array(35 - totalSlots.length)
				.fill(0)
				.map((_, i) => (
					<Day
						value={i + 1}
						isOnRange={isOnRange}
						isOnPreviewRange={isOnPreviewRange}
						setMouseIsDown={setMouseIsDown}
						dateObject={dateObject}
						onMouseDown={date => handleMouseDown(date)}
						onMouseUp={() => handleMouseUp()}
						month={moment(dateObject).add(1, 'months')}
						onMouseOver={date => handleMouseOver(date)}
						empty={true}
						key={totalSlots.length + i}
					/>
				))
		);
	}
	return (
		<>
			<div className={styles.weekDays}>
				{weekDayShort.map((day, i) => (
					<div key={i}>
						<span>{day}</span>
					</div>
				))}
			</div>
			<div
				className={styles.days}
				onMouseLeave={() => {
					setPreviewRange(null);
					setTempRange(null);
				}}
			>
				{totalSlots}
			</div>
		</>
	);
};

const Day = ({
	dateObject,
	value,
	month,
	onMouseDown,
	onMouseUp,
	onMouseOver,
	isOnRange,
	isOnPreviewRange,
	empty = false,
}) => {
	const date = month.set('date', value).format('MM/DD/YYYY');
	const { selected, isFirst, isLast } = isOnRange(date);
	const { isFirst: isFirst_p, isLast: isLast_p, preview } = isOnPreviewRange(date);
	return (
		<div
			className={styles.day}
			data-selected={selected}
			data-preview={preview}
			data-first={isFirst || isFirst_p}
			data-last={isLast || isLast_p}
			onMouseOver={() => onMouseOver(date)}
			onMouseDown={() => onMouseDown(date)}
			onMouseUp={() => onMouseUp(false)}
			onTouchStart={() => onMouseDown(date)}
			onTouchEnd={() => onMouseUp(false)}
		>
			<span className={styles.selected} />
			<span className={styles.preview} />
			<span data-empty={empty} data-selected={selected} data-preview={preview}>
				{value}
			</span>
		</div>
	);
};

const MonthPicker = ({ active, onChange }) => {
	const months = moment.months();
	return (
		<div className={styles.months}>
			{months.map((month, i) => {
				let attrs = active === month ? { 'data-active': true } : {};
				return (
					<div key={i}>
						<span {...attrs} onClick={() => onChange(month)}>
							{month}
						</span>
					</div>
				);
			})}
		</div>
	);
};

const YearsPicker = ({ active, onChange }) => {
	const years = new Array(12).fill(0).map((_, i) => 2019 + i);
	return (
		<div className={styles.months}>
			{years.map((year, i) => {
				let attrs = active === year ? { 'data-active': true } : {};
				return (
					<div key={i}>
						<span {...attrs} onClick={() => onChange(year)}>
							{year}
						</span>
					</div>
				);
			})}
		</div>
	);
};
